import React, { useCallback, useEffect, useRef } from 'react'
import { useForm, useWatch, useController } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Alert,
  Button,
  Popconfirm,
  Tooltip,
} from '@surecompanies/core_components'
import RHFInput from '../../PlanBenefitOverrides/components/form/RHFInput'
import RHFSelect from '../../PlanBenefitOverrides/components/form/RHFSelect'
import { useDebounce } from 'hooks/useDebounce'
import OverrideAuditStrip from '../../../components/OverrideAuditStrip'
import {
  PlanAndRateOverrideFormSchema,
  REQUIRED_PARAMS_FIELDS,
  apiPayloadToForm,
  flatToApiPayload,
  type PlanAndRateViewMetadata,
  type PlanAndRateOverrideFormValues,
  type PlanAndRateServerFieldError,
} from './planAndRateOverrideForm.schema'
import { normalizeParams } from './planAndRateOverrideForm.helpers'
import { US_STATE_OPTIONS } from '../../../constants'
import {
  useCheckDuplicatePlanAndRateOverrideRule,
  PLAN_AND_RATE_OVERRIDE_CHECK_DUPLICATE_QUERY_KEY,
} from '../hooks/useCheckDuplicatePlanAndRateOverrideRule'
import { useDeletePlanAndRateOverrideRule } from '../hooks/useDeletePlanAndRateOverrideRule'
import { useQueryClient } from '@tanstack/react-query'
import type {
  CreatePlanAndRateOverrideRuleRequestDto,
  PlanAndRateOverrideParams,
} from '../../../api/dto/plan-and-rate-override-rules.dto'
import { relativeTimeString } from 'enrollment-platform/utils/relative-time-string'
import styles from '../../../components/PlanOverridesComponents.module.scss'
import PlanAndRateParamsSingleRowGrid, {
  type PlanAndRateParamsSingleRowGridHandle,
} from './PlanAndRateParamsSingleRowGrid'

const DUPLICATE_CHECK_DEBOUNCE_MS = 400
const CURRENT_YEAR = new Date().getFullYear()

export type PlanAndRateFormMode = 'create' | 'edit' | 'view'

export type { PlanAndRateServerFieldError }

interface Props {
  mode?: PlanAndRateFormMode
  defaultValues?: Partial<PlanAndRateOverrideFormValues>
  serverErrors?: PlanAndRateServerFieldError[]
  metadata?: PlanAndRateViewMetadata
  onSubmit?: (data: CreatePlanAndRateOverrideRuleRequestDto) => Promise<void>
  onCancel?: () => void
  isSubmitting?: boolean
  onDuplicateFound?: (ruleId: string) => void
  step?: number
  extraContent?: React.ReactNode
}

export default function PlanAndRateOverrideForm({
  mode = 'create',
  defaultValues,
  serverErrors,
  metadata,
  onSubmit,
  onCancel,
  isSubmitting = false,
  onDuplicateFound,
  step,
  extraContent,
}: Props) {
  const isEditMode = mode === 'edit'
  const isReadOnly = mode === 'view'
  const isCreateMode = mode === 'create'

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, submitCount },
  } = useForm<PlanAndRateOverrideFormValues>({
    resolver: zodResolver(PlanAndRateOverrideFormSchema),
    defaultValues: {
      year: CURRENT_YEAR,
      state: '',
      hiosPlanId: '',
      params: normalizeParams({}),
      ...defaultValues,
    },
  })

  useEffect(() => {
    if (!serverErrors || serverErrors.length === 0) return
    serverErrors.forEach(({ field, errors: fieldErrors }) => {
      if (!field || fieldErrors.length === 0) return
      // match.year → year, match.state → state, match.hiosPlanId → hiosPlanId
      // params.carrier → params.carrier (keep prefix; RHF supports dot notation for nested fields)
      const mappedField = field.startsWith('match.')
        ? field.slice('match.'.length)
        : field
      setError(mappedField as keyof PlanAndRateOverrideFormValues, {
        type: 'server',
        message: fieldErrors.join(', '),
      })
    })
  }, [serverErrors, setError])

  const year = useWatch({ control, name: 'year' })
  const state = useWatch({ control, name: 'state' })
  const hiosPlanId = useWatch({ control, name: 'hiosPlanId' })

  const { field: paramsField } = useController({ control, name: 'params' })
  const paramsGridRef = useRef<PlanAndRateParamsSingleRowGridHandle>(null)

  const duplicateKey =
    isCreateMode && year && state && hiosPlanId
      ? `${year}|${state}|${hiosPlanId}`
      : null
  const debouncedDuplicateKey = useDebounce(
    duplicateKey,
    DUPLICATE_CHECK_DEBOUNCE_MS
  )
  const duplicateQuery = debouncedDuplicateKey
    ? { year: Number(year), state, hiosPlanId }
    : {}
  const { data: duplicateData } =
    useCheckDuplicatePlanAndRateOverrideRule(duplicateQuery)
  const showDuplicateAlert = duplicateData?.exists === true
  const duplicateRule = duplicateData?.exists ? duplicateData.rule : null

  const queryClient = useQueryClient()
  const { mutate: deleteDuplicate, isPending: isDeletingDuplicate } =
    useDeletePlanAndRateOverrideRule()

  const handleDeleteDuplicate = useCallback(() => {
    if (!duplicateRule) return
    deleteDuplicate(duplicateRule.id, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [PLAN_AND_RATE_OVERRIDE_CHECK_DUPLICATE_QUERY_KEY],
        })
      },
    })
  }, [duplicateRule, deleteDuplicate, queryClient])

  const handleParamsChange = useCallback(
    (newParams: PlanAndRateOverrideParams) => {
      paramsField.onChange(newParams)
    },
    [paramsField]
  )

  const handleFormSubmit = useCallback(
    async (values: PlanAndRateOverrideFormValues) => {
      if (!onSubmit) return
      const payload = flatToApiPayload(values)
      await onSubmit(payload)
    },
    [onSubmit]
  )

  const handleFormSubmitWrapper = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      const latestParams = paramsGridRef.current?.getLatestParams()
      if (latestParams !== undefined) {
        setValue('params', latestParams, {
          shouldValidate: false,
          shouldDirty: false,
        })
      }
      return handleSubmit(handleFormSubmit)(e)
    },
    [handleSubmit, setValue, handleFormSubmit]
  )

  return (
    <form
      onSubmit={handleFormSubmitWrapper}
      noValidate
      className={isReadOnly || isEditMode ? styles.readOnlyForm : undefined}
    >
      {isCreateMode && showDuplicateAlert && duplicateRule && (
        <div className={styles.duplicateBanner}>
          <div className={styles.duplicateBannerIcon}>⚠</div>
          <div className={styles.duplicateBannerBody}>
            <span className={styles.duplicateBannerTitle}>
              Rule already exists for this combination
            </span>
            <span className={styles.duplicateBannerValue}>
              {duplicateRule.match.state}
              <span className={styles.duplicateBannerDot}> · </span>
              {duplicateRule.match.hiosPlanId}
              <span className={styles.duplicateBannerDot}> · </span>
              {duplicateRule.match.year}
            </span>
            <span className={styles.duplicateBannerMeta}>
              <span
                className={`${styles.statusTag} ${duplicateRule.status === 'APPROVED' ? styles.statusActive : duplicateRule.status === 'REJECTED' ? styles.statusInactive : styles.statusDraft}`}
              >
                {duplicateRule.status}
              </span>
              <span className={styles.duplicateBannerDot}>·</span>
              {relativeTimeString(duplicateRule.updatedAt)}
            </span>
          </div>
          <div className={styles.duplicateBannerActions}>
            <Button
              type="primary"
              size="small"
              onClick={() => onDuplicateFound?.(duplicateRule.id)}
              disabled={!onDuplicateFound}
            >
              Edit
            </Button>
            <Popconfirm
              title="Delete this rule?"
              description="This will permanently delete the existing rule."
              okText="Delete"
              okType="danger"
              cancelText="Cancel"
              onConfirm={handleDeleteDuplicate}
            >
              <Button
                type="default"
                size="small"
                danger
                loading={isDeletingDuplicate}
              >
                Delete
              </Button>
            </Popconfirm>
          </div>
        </div>
      )}

      {!isReadOnly &&
        submitCount > 0 &&
        REQUIRED_PARAMS_FIELDS.some(({ key }) => {
          const pe = errors.params as
            | Record<string, { message?: string } | undefined>
            | undefined
          return pe?.[key]?.message
        }) && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="Required fields are missing in Override data"
            description={
              <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                {REQUIRED_PARAMS_FIELDS.map(({ key, label: _label }) => {
                  const pe = errors.params as
                    | Record<string, { message?: string } | undefined>
                    | undefined
                  const msg = pe?.[key]?.message
                  return msg ? <li key={key}>{msg}</li> : null
                })}
              </ul>
            }
          />
        )}

      <div className={styles.stepList}>
        {/* Step 1 — Target (immutable after create) */}
        {(step === undefined || step === 1) && (
          <div className={styles.step}>
            <div className={styles.stepLeft}>
              <div className={styles.stepBadge}>1</div>
              <div className={styles.stepRail} />
            </div>
            <div className={styles.stepContent}>
              <p className={styles.stepTitle}>Target</p>
              <div className={styles.stepFieldRow}>
                <RHFInput
                  name="year"
                  control={control}
                  label="Year"
                  required
                  type="number"
                  placeholder="e.g. 2025"
                  disabled={isReadOnly || isEditMode}
                />
                <RHFSelect
                  name="state"
                  control={control}
                  label="State"
                  required
                  options={US_STATE_OPTIONS}
                  placeholder="Select state"
                  showSearch
                  optionFilterProp="label"
                  disabled={isReadOnly || isEditMode}
                />
                <RHFInput
                  name="hiosPlanId"
                  control={control}
                  label="HIOS Plan ID"
                  required
                  placeholder="e.g. 12345AB0012345"
                  disabled={isReadOnly || isEditMode}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2 — Override params (editable) */}
        {(step === undefined || step === 2) && (
          <div className={styles.step}>
            <div className={styles.stepLeft}>
              <div className={styles.stepBadge}>2</div>
              {extraContent && <div className={styles.stepRail} />}
            </div>
            <div
              className={`${styles.stepContent} ${extraContent ? '' : styles.stepContentLast}`}
            >
              <p className={styles.stepTitle}>Override data</p>
              <PlanAndRateParamsSingleRowGrid
                ref={paramsGridRef}
                params={paramsField.value ?? {}}
                mode={isReadOnly ? 'view' : isEditMode ? 'edit' : 'create'}
                onChange={handleParamsChange}
              />
            </div>
          </div>
        )}

        {/* Step 3 — Evidence documents */}
        {(step === undefined || step === 3) && extraContent && (
          <div className={styles.step}>
            <div className={styles.stepLeft}>
              <div className={styles.stepBadge}>3</div>
            </div>
            <div className={`${styles.stepContent} ${styles.stepContentLast}`}>
              {extraContent}
            </div>
          </div>
        )}
      </div>

      {!isReadOnly && (
        <div className={styles.formFooter}>
          {isEditMode && metadata ? (
            <OverrideAuditStrip metadata={metadata} />
          ) : (
            <div />
          )}
          <div className={styles.formFooterActions}>
            <Button type="default" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
            <Tooltip
              title={
                showDuplicateAlert
                  ? 'Resolve the duplicate rule above before saving.'
                  : !isEditMode && !year
                    ? 'Enter a year before saving.'
                    : !isEditMode && !state
                      ? 'Select a state before saving.'
                      : !isEditMode && !hiosPlanId
                        ? 'Enter a HIOS Plan ID before saving.'
                        : undefined
              }
            >
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={
                  isSubmitting ||
                  showDuplicateAlert ||
                  (!isEditMode && (!year || !state || !hiosPlanId))
                }
              >
                Save
              </Button>
            </Tooltip>
          </div>
        </div>
      )}
    </form>
  )
}

export { apiPayloadToForm }
